import { useEffect, useRef, useState } from "react";

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return "0:00";
  const total = Math.floor(seconds);
  const min = Math.floor(total / 60);
  const sec = (total % 60).toString().padStart(2, "0");
  return `${min}:${sec}`;
}

export function AudioTrack({
  src,
  title,
  artist,
}: {
  src: string;
  title: string;
  artist: string;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMetadata = () => setDuration(audio.duration);
    const onEnded = () => setIsPlaying(false);

    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("ended", onEnded);

    return () => {
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("ended", onEnded);
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play();
    } else {
      audio.pause();
    }
  };

  const seekFromEvent = (clientX: number) => {
    const audio = audioRef.current;
    const bar = progressRef.current;
    if (!audio || !bar || !duration) return;
    const rect = bar.getBoundingClientRect();
    const pct = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    audio.currentTime = pct * duration;
  };

  const progressPct = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div
      className="mt-4 flex items-center gap-4 py-3 px-4 border"
      style={{ borderColor: "#d6c5a8" }}
    >
      <audio ref={audioRef} src={src} preload="metadata" />
      <button
        onClick={togglePlay}
        aria-label={isPlaying ? "Pause" : "Play"}
        className="w-10 h-10 shrink-0 flex items-center justify-center border rounded-full border-[#2b1d12] text-[#2b1d12] bg-transparent transition-colors hover:bg-[#2b1d12] hover:text-[#faf5ec]"
      >
        {isPlaying ? (
          <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="currentColor"
            aria-hidden
          >
            <rect x="6" y="5" width="4" height="14" />
            <rect x="14" y="5" width="4" height="14" />
          </svg>
        ) : (
          <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="currentColor"
            aria-hidden
          >
            <path d="M7 5v14l12-7z" />
          </svg>
        )}
      </button>
      <div className="flex-1 min-w-0">
        <div
          className="italic truncate leading-tight"
          style={{ color: "#2b1d12" }}
        >
          {title}
        </div>
        <div
          className="text-[10px] tracking-[0.25em] uppercase truncate mt-1 flex justify-between gap-2"
          style={{ color: "#8a6a3b" }}
        >
          <span className="truncate">{artist}</span>
          <span className="shrink-0 tabular-nums">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
        </div>
        <div
          ref={progressRef}
          onClick={(e) => seekFromEvent(e.clientX)}
          className="mt-2 h-px w-full cursor-pointer"
          style={{ backgroundColor: "#e3d6bf" }}
        >
          <div
            className="h-px"
            style={{
              width: `${progressPct}%`,
              backgroundColor: "#8a6a3b",
            }}
          />
        </div>
      </div>
    </div>
  );
}
